import { useEffect, useState, type ImgHTMLAttributes } from "react";
import { diskPreviewUrl } from "../../lib/media/diskPreviewUrl";
import { getPublicMediaUrl } from "../../lib/media/getPublicMediaUrl";

const PREVIEW_ATTEMPTS = 20;
const PREVIEW_RETRY_MS = 1000;

type DiskImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  /** Сохранённый URL формы (`…/content` или обычный). */
  src: string;
};

function probeImage(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = url;
  });
}

/** Сразу `/content`, когда `/image-preview` готов — подменяет src в UI. */
export function DiskImage({ src, alt = "", onError, ...imgProps }: DiskImageProps) {
  const contentUrl = getPublicMediaUrl(src);
  const previewUrl = diskPreviewUrl(contentUrl);
  const [current, setCurrent] = useState(contentUrl);

  useEffect(() => {
    setCurrent(contentUrl);
    if (!previewUrl || previewUrl === contentUrl) return;

    let cancelled = false;

    const tryPreview = async () => {
      for (let attempt = 0; attempt < PREVIEW_ATTEMPTS; attempt += 1) {
        const ok = await probeImage(previewUrl);
        if (cancelled) return;
        if (ok) {
          setCurrent(previewUrl);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, PREVIEW_RETRY_MS));
        if (cancelled) return;
      }
    };

    void tryPreview();
    return () => {
      cancelled = true;
    };
  }, [contentUrl, previewUrl]);

  return (
    <img
      {...imgProps}
      src={current}
      alt={alt}
      onError={(event) => {
        if (current !== contentUrl) setCurrent(contentUrl);
        onError?.(event);
      }}
    />
  );
}
